<template>
	<div class="flex items-center">
		<BaseButton class="px-3 sm:px-5 mr-2 sm:mr-3" @click="onPrevClick" :disabled="modelValue == 1" type="tertiary">
			<BaseIcon icon="ic:outline-arrow-back" :color="modelValue == 1 ? '#969BA5' : '#1E2328'" />
			<span class="hidden sm:inline-block ml-[10px]" v-show="modelValue != 1">{{ prevLang }}</span>
		</BaseButton>

		<div class="mr-2 ml-1 sm:mr-6 sm:ml-5" v-show="modelValue - steps > 1">
			<BaseIcon icon="ic:outline-more-horiz" color="#969BA5" />
		</div>

		<div
			@click="selectPage(page)"
			v-for="(page, i) in numbers"
			:key="i"
			:class="[
				'grid place-items-center min-w-30 h-dj-30 sm:min-w-40 sm:h-dj-40 rounded-full font-medium cursor-pointer',
				modelValue == page ? 'text-dj-blue bg-dj-gray-transparency' : 'text-dj-gray',
			]"
		>
			{{ page }}
		</div>

		<div class="mr-1 ml-2 sm:mr-5 sm:ml-6" v-show="modelValue + steps < total">
			<BaseIcon icon="ic:outline-more-horiz" color="#969BA5" />
		</div>

		<BaseButton
			class="px-3 sm:px-5 ml-2 sm:ml-3"
			@click="onNextClick"
			:disabled="modelValue == total"
			type="tertiary"
		>
			<span class="hidden sm:inline-block mr-[10px]" v-show="modelValue != total">{{ nextLang }}</span>
			<BaseIcon icon="ic:outline-arrow-forward" :color="modelValue == total ? '#969BA5' : '#1E2328'" />
		</BaseButton>
	</div>
</template>

<script lang="ts">
import { defineComponent, computed, watch, ref } from "vue";
import BaseButton from "~/ui-kit/components/BaseButton.vue";
import BaseIcon from "~/ui-kit/components/BaseIcon.vue";

export default defineComponent({
	name: "BasePagination",
	components: {
		BaseButton,
		BaseIcon,
	},
	props: {
		modelValue: {
			type: Number,
			required: true,
		},
		total: {
			type: Number,
			required: true,
		},
		steps: {
			type: Number,
			default: 2,
			validator: (value: number) => {
				return value >= 2 && value <= 4;
			},
		},
		nextLang: {
			type: String,
			default: "Következő",
		},
		prevLang: {
			type: String,
			default: "Előző",
		},
	},
	emits: ["update:modelValue", "changePage"],
	setup(props, { emit }) {
		const pagesArray = ref(Array.from({ length: props.total }, (_, k) => k + 1));

		watch(
			() => props.total,
			(total) => {
				pagesArray.value = Array.from({ length: total }, (_, k) => k + 1);
			}
		);

		function selectPage(page: number) {
			emit("update:modelValue", page);
			emit("changePage", page);
		}

		function onPrevClick() {
			emit("update:modelValue", props.modelValue - 1);
			emit("changePage", props.modelValue - 1);
		}

		function onNextClick() {
			emit("update:modelValue", props.modelValue + 1);
			emit("changePage", props.modelValue + 1);
		}

		const numbers = computed(() => {
			let chunk: number[] = [];

			const currentIndex = props.modelValue - 1;

			if (props.modelValue - props.steps <= 0) {
				chunk = pagesArray.value.slice(0, 1 + props.steps * 2);
			} else if (props.modelValue + props.steps >= props.total) {
				chunk = pagesArray.value.slice(pagesArray.value[props.total - props.steps * 2 - 2], props.total);
			} else {
				chunk = pagesArray.value.slice(currentIndex - props.steps, currentIndex + props.steps + 1);
			}

			return chunk;
		});

		return {
			numbers,
			selectPage,
			onPrevClick,
			onNextClick,
		};
	},
});
</script>
